/**
 * Simple logger system with the possibility of registering custom outputs.
 *
 * 4 different log levels are provided, with corresponding methods:
 * - debug   : for debug information
 * - info    : for informative status of the application (success, ...)
 * - warning : for non-critical errors that do not prevent normal application behavior
 * - error   : for critical errors that prevent normal application behavior
 *
 * Example usage:
 * ```
 * import { Logger } from 'app/core/logger.service';
 *
 * const log = new Logger('myFile');
 * ...
 * log.debug('something happened');
 * ```
 *
 * To disable debug and info logs in production, add this snippet to your root component:
 * ```
 * export class AppComponent implements OnInit {
 *   ngOnInit() {
 *     if (environment.production) {
 *       Logger.enableProductionMode();
 *     }
 *     ...
 *   }
 * }
 *
 * If you want to process logs through other outputs than console, you can add LogOutput functions to Logger.outputs.
 */

/**
 * The possible log levels.
 * LogLevel.Off is never emitted and only used with Logger.level property to disable logs.
 */
export enum LogLevel {
  Off = 0,
  Error,
  Warning,
  Info,
  Debug,
}

/**
 * Log output handler function.
 */
export type LogOutput = (source: string, level: LogLevel, ...objects: any[]) => void;

export class Logger {
  /**
   * Current logging level.
   * Set it to LogLevel.Off to disable logs completely.
   */
  static level = LogLevel.Debug;

  /**
   * Additional log outputs.
   */
  static outputs: LogOutput[] = [];

  /**
   *  Warren Dowey
   *
   *  enableProductionMode
   *
   *  Enables production mode.
   * Sets logging level to LogLevel.Warning.
   */
  static enableProductionMode() {
    Logger.level = LogLevel.Warning;
  }

  constructor(private source?: string) {}

  /**
   *  Warren Dowey
   *
   *  debug
   *
   *  Logs messages or objects  with the debug level.
   * Works the same as console.log().
   *
   * @param objects: any[]
   *
   * @returns outputs to any debugging received to the logger function
   */
  debug(...objects: any[]) {
    this.log(console.log, LogLevel.Debug, objects);
  }

  /**
   *  Warren Dowey
   *
   *  info
   *
   *  Logs messages or objects  with the info level.
   * Works the same as console.log().
   *
   * @param objects: any[]
   *
   * @returns outputs to any info received to the logger function
   */
  info(...objects: any[]) {
    // tslint:disable-next-line: no-console
    this.log(console.info, LogLevel.Info, objects);
  }

  /**
   * Logs messages or objects  with the warning level.
   * Works the same as console.log().
   *
   * @param objects: any[]
   *
   * @returns outputs to any warnings received to the logger function
   */
  warn(...objects: any[]) {
    objects.push(console.trace());
    this.log(console.warn, LogLevel.Warning, objects);
  }

  /**
   *  Warren Dowey
   *
   *  error
   *
   *  Logs messages or objects  with the error level.
   * Works the same as console.log().
   *
   * @param objects: any[]
   *
   * @returns outputs to any errors received to the logger function
   */
  error(...objects: any[]) {
    objects.push(console.trace());
    this.log(console.error, LogLevel.Error, objects);
  }

  /**
   *  Warren Dowey
   *
   *  log
   *
   *  logs any errors with specifc functions allowed including .error, .debug, .warn and .info
   * this will provide to the user some custom wording that the regular console.log wouldn't provide
   *
   * @param func: Function
   * @param level: LogLevel
   * @param objects: any[]
   *
   * @returns outputs to the logger the message to be sent
   */
  private log(func: Function, level: LogLevel, objects: any[]) {
    if (level <= Logger.level) {
      const log = this.source ? ['[' + this.source + ']'].concat(objects) : objects;
      func.apply(console, log);
      Logger.outputs.forEach((output) =>
        output.apply(output, [this.source, level].concat(objects))
      );
    }
  }
}
